// src/pages/delivery/DeliveryBoard.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api, { isLocalInstall } from '../../config/api';
import useAuthStore from '../../store/authStore';
import { usePushSubscription } from '../../hooks/usePushSubscription';

const GREEN   = '#2e9f86';
const AMBER   = '#f59e0b';
const BLUE    = '#3b82f6';
const RED     = '#ef4444';

const STATUS_CONFIG = {
  pending:    { label: 'Pending',    color: AMBER,  bg: '#fffbeb', border: '#fcd34d', emoji: '🟡' },
  dispatched: { label: 'Dispatched', color: BLUE,   bg: '#eff6ff', border: '#93c5fd', emoji: '🔵' },
  delivered:  { label: 'Delivered',  color: GREEN,  bg: '#f0fdf4', border: '#86efac', emoji: '✅' },
  failed:     { label: 'Failed',     color: RED,    bg: '#fef2f2', border: '#fca5a5', emoji: '❌' },
};

const NEXT_STATUS = {
  pending:    'dispatched',
  dispatched: 'delivered',
};

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const pad = (x) => String(x).padStart(2, '0');
const fmtLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayLocal = () => fmtLocal(new Date());

/* ─── responsive hook ───────────────────────────────────────────────────── */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

export default function DeliveryBoard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const user = useAuthStore((s) => s.user);
  const isDriver = user?.role === 'driver';
  const { supported: pushSupported, permission: pushPermission, subscribe: pushSubscribe } = usePushSubscription(false);
  const [activeTab, setActiveTab] = useState('board');
  const [boardDate, setBoardDate] = useState(todayLocal());

  const TABS = isDriver
    ? [{ key: 'board', label: '🚚 Board' }]
    : [
        { key: 'board',   label: '🚚 Board' },
        { key: 'summary', label: '📋 Driver Summary' },
      ];

  const { data: rawDocs = [], isLoading } = useQuery(
    ['delivery-docs', boardDate],
    () => api.get('/api/documents', { params: { type: 200, status: 'confirmed', date_from: boardDate, date_to: boardDate, all: true } }).then(r => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      return arr.filter(d => d?.delivery?.enabled);
    }),
    { refetchInterval: 30000 },
  );

  // drivers list — only fetched for roles that can assign (admin/manager/cashier)
  const { data: drivers = [] } = useQuery(
    ['drivers'],
    () => api.get('/api/users/drivers').then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])),
    { enabled: !isDriver },
  );

  const driverName = (id) => {
    if (!id) return '—';
    if (user && String(user._id) === String(id)) return user.name;
    const u = drivers.find(u => String(u._id) === String(id));
    return u ? u.name : '—';
  };

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Push notification prompt for drivers — cloud only */}
      {isDriver && pushSupported && !isLocalInstall && pushPermission !== 'granted' && pushPermission !== 'denied' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
          <span style={{ fontSize: 13, color: '#92400e' }}>🔔 فعّل الإشعارات لتصلك طلبات التوصيل تلقائياً</span>
          <button
            onClick={pushSubscribe}
            style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            تفعيل
          </button>
        </div>
      )}
      {isDriver && pushSupported && !isLocalInstall && pushPermission === 'denied' && (
        <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
          ⚠️ الإشعارات محجوبة في المتصفح — اذهب إلى إعدادات المتصفح ← صلاحيات الموقع ← السماح بالإشعارات
        </div>
      )}
      {isDriver && pushSupported && !isLocalInstall && pushPermission === 'granted' && (
        <div style={{ fontSize: 12, color: '#065f46', background: '#f0fdf8', border: '1px solid #6ee7b7', borderRadius: 10, padding: '8px 14px', marginBottom: 14 }}>
          🔔 الإشعارات مفعّلة — ستصلك طلبات التوصيل تلقائياً
        </div>
      )}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#111', margin: 0 }}>🚚 Delivery</h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Auto-refreshes every 30 seconds</p>
        </div>
        <button
          onClick={() => { qc.invalidateQueries(['delivery-docs', boardDate]); qc.invalidateQueries(['driver-summary-docs']); }}
          style={{ padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${GREEN}`, color: GREEN, background: '#f0fdf8', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Tabs */}
      {TABS.length > 1 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1.5px solid #e5e7eb' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: 'none', background: 'none',
                borderBottom: activeTab === tab.key ? `2.5px solid ${GREEN}` : '2.5px solid transparent',
                color: activeTab === tab.key ? GREEN : '#6b7280',
                marginBottom: -1.5,
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'board' && (
        <BoardTab
          rawDocs={rawDocs}
          isLoading={isLoading}
          drivers={drivers}
          driverName={driverName}
          isDriver={isDriver}
          isMobile={isMobile}
          qc={qc}
          boardDate={boardDate}
          setBoardDate={setBoardDate}
        />
      )}
      {activeTab === 'summary' && !isDriver && (
        <SummaryTab
          rawDocs={rawDocs}
          drivers={drivers}
          isLoading={isLoading}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

/* ─── Board Tab ─────────────────────────────────────────────────────────── */
function BoardTab({ rawDocs, isLoading, drivers, driverName, isDriver, isMobile, qc, boardDate, setBoardDate }) {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDriver, setFilterDriver] = useState('all');
  const [search,       setSearch]       = useState('');
  const [selectedId,   setSelectedId]   = useState(null);

  const activeDriverIds = useMemo(() => {
    const ids = new Set(rawDocs.map(d => d.delivery?.driver_id).filter(Boolean).map(String));
    return [...ids];
  }, [rawDocs]);

  const docs = useMemo(() => {
    let list = rawDocs;
    if (filterStatus !== 'all') list = list.filter(d => d.delivery?.status === filterStatus);
    if (filterDriver !== 'all') list = list.filter(d => String(d.delivery?.driver_id) === filterDriver);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.delivery?.address?.toLowerCase().includes(q) ||
        d.delivery?.phone?.includes(q) ||
        d.number?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [rawDocs, filterStatus, filterDriver, search]);

  const counts = useMemo(() => ({
    all:        rawDocs.length,
    pending:    rawDocs.filter(d => d.delivery?.status === 'pending').length,
    dispatched: rawDocs.filter(d => d.delivery?.status === 'dispatched').length,
    delivered:  rawDocs.filter(d => d.delivery?.status === 'delivered').length,
    failed:     rawDocs.filter(d => d.delivery?.status === 'failed').length,
  }), [rawDocs]);

  const updateStatus = useMutation(
    ({ id, status }) => api.patch(`/api/documents/${id}/delivery`, { status }),
    { onSuccess: () => qc.invalidateQueries(['delivery-docs']), onError: (e) => alert(e?.response?.data?.error || e.message) }
  );
  const assignDriver = useMutation(
    ({ id, driver_id }) => api.patch(`/api/documents/${id}/delivery`, { driver_id }),
    { onSuccess: () => qc.invalidateQueries(['delivery-docs']), onError: (e) => alert(e?.response?.data?.error || e.message) }
  );

  const handleAdvance = (doc, e) => {
    e.stopPropagation();
    const next = NEXT_STATUS[doc.delivery?.status];
    if (!next) return;
    updateStatus.mutate({ id: doc._id, status: next });
  };
  const handleFail = (doc, e) => {
    e.stopPropagation();
    if (!window.confirm('Mark this order as failed?')) return;
    updateStatus.mutate({ id: doc._id, status: 'failed' });
  };

  const selectedDoc = selectedId ? (rawDocs.find(d => d._id === selectedId) || null) : null;

  // Driver balance: sum of delivered+unpaid orders
  const driverBalance = useMemo(() => {
    if (!isDriver) return null;
    const pending = rawDocs.filter(d => d.delivery?.status === 'delivered' && Number(d.totals?.balance) > 0);
    const total = pending.reduce((s, d) => s + Number(d.totals?.balance || 0), 0);
    return { total, count: pending.length };
  }, [rawDocs, isDriver]);

  return (
    <>
      {/* Driver balance card */}
      {isDriver && driverBalance && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: driverBalance.total > 0 ? '#fef3c7' : '#f0fdf8',
          border: `1.5px solid ${driverBalance.total > 0 ? '#fcd34d' : '#6ee7b7'}`,
          borderRadius: 12, padding: '12px 16px', marginBottom: 16,
        }}>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>
              رصيد بذمتك
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: driverBalance.total > 0 ? '#92400e' : '#065f46' }}>
              ${money(driverBalance.total)}
            </div>
            {driverBalance.count > 0 && (
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {driverBalance.count} طلب{driverBalance.count !== 1 ? 'ات' : ''} مسلّمة غير مسدّدة
              </div>
            )}
          </div>
          {driverBalance.total === 0 && (
            <div style={{ fontSize: 28 }}>✅</div>
          )}
          {driverBalance.total > 0 && (
            <div style={{ fontSize: 28 }}>💵</div>
          )}
        </div>
      )}

      {/* date picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
          <span style={{ fontSize: 14 }}>📅</span>
          <input
            type="date"
            value={boardDate}
            onChange={e => setBoardDate(e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: 13, fontWeight: 600, color: '#111', background: 'transparent', cursor: 'pointer' }}
          />
        </div>
        {boardDate !== todayLocal() && (
          <button
            onClick={() => setBoardDate(todayLocal())}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1.5px solid ${GREEN}`, background: '#f0fdf8', color: GREEN, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            Today
          </button>
        )}
        {boardDate !== todayLocal() && (
          <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
            Showing history for {new Date(boardDate + 'T12:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        )}
      </div>

      {/* status filter pills — horizontal scroll on mobile */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16,
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: isMobile ? 6 : 0,
      }}>
        {[
          { key: 'all',        label: 'All',           color: '#6b7280' },
          { key: 'pending',    label: '🟡 Pending',    color: AMBER },
          { key: 'dispatched', label: '🔵 Dispatched', color: BLUE },
          { key: 'delivered',  label: '✅ Delivered',  color: GREEN },
          { key: 'failed',     label: '❌ Failed',     color: RED },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilterStatus(tab.key)}
            style={{
              padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: `1.5px solid ${filterStatus === tab.key ? tab.color : '#e5e7eb'}`,
              background: filterStatus === tab.key ? tab.color : '#fff',
              color: filterStatus === tab.key ? '#fff' : '#374151',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            {tab.label} {counts[tab.key] > 0 && <span style={{ opacity: 0.8 }}>({counts[tab.key]})</span>}
          </button>
        ))}
      </div>

      {/* search + driver filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search address, phone, order #…"
          style={{ flex: 1, minWidth: isMobile ? '100%' : 200, border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}
        />
        {!isDriver && activeDriverIds.length > 0 && (
          <select
            value={filterDriver}
            onChange={e => setFilterDriver(e.target.value)}
            style={{ flex: isMobile ? 1 : 'none', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, background: '#fff' }}
          >
            <option value="all">All drivers</option>
            {activeDriverIds.map(id => (
              <option key={id} value={id}>{driverName(id)}</option>
            ))}
          </select>
        )}
      </div>

      {isLoading && <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>Loading deliveries…</div>}

      {!isLoading && docs.length === 0 && (
        <div style={{ textAlign: 'center', padding: 64, color: '#9ca3af' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚚</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>No delivery orders found</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>
            {filterStatus !== 'all' ? 'Try changing the status filter' : boardDate !== todayLocal() ? 'No deliveries on this date' : (isDriver ? 'No deliveries assigned to you yet' : 'Create a delivery order from the POS screen')}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {docs.map(doc => {
          const status = doc.delivery?.status || 'pending';
          const cfg    = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
          const next   = NEXT_STATUS[status];
          const fee    = Number(doc.delivery?.fee) || 0;
          const total  = Number(doc.total_usd || doc.totals?.total || 0);

          return (
            <div
              key={doc._id}
              onClick={() => setSelectedId(doc._id)}
              style={{ background: '#fff', border: `1.5px solid ${cfg.border}`, borderRadius: 12, padding: 16, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'box-shadow .15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{doc.number || doc._id}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{fmtDate(doc.createdAt)}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap' }}>
                  {cfg.emoji} {cfg.label}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 16 }}>📍</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111', wordBreak: 'break-word' }}>{doc.delivery?.address || '—'}</div>
                  {doc.delivery?.phone && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      📞 <a href={`tel:${doc.delivery.phone}`} onClick={e => e.stopPropagation()} style={{ color: BLUE, textDecoration: 'none' }}>{doc.delivery.phone}</a>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 14 }}>🧑‍✈️</span>
                {!isDriver && drivers.length > 0 && status === 'pending' ? (
                  <select
                    value={doc.delivery?.driver_id || ''}
                    onClick={e => e.stopPropagation()}
                    onChange={e => assignDriver.mutate({ id: doc._id, driver_id: e.target.value })}
                    style={{ fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', flex: 1, background: '#fff' }}
                  >
                    <option value="">— Assign driver —</option>
                    {drivers.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                  </select>
                ) : (
                  <span style={{ fontSize: 13, color: doc.delivery?.driver_id ? '#111' : '#9ca3af' }}>
                    {driverName(doc.delivery?.driver_id)}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 12, flexWrap: 'wrap', gap: 4 }}>
                <span>Order: <strong style={{ color: '#111' }}>${money(total)}</strong></span>
                {fee > 0 && <span>+ Fee: <strong style={{ color: '#111' }}>${money(fee)}</strong></span>}
              </div>

              {doc.delivery?.notes && (
                <div style={{ fontSize: 11, color: '#6b7280', background: '#f9fafb', borderRadius: 6, padding: '5px 8px', marginBottom: 10, wordBreak: 'break-word' }}>
                  💬 {doc.delivery.notes}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                {next && (
                  <button
                    onClick={e => handleAdvance(doc, e)}
                    disabled={updateStatus.isLoading}
                    style={{ flex: 1, padding: isMobile ? '12px 0' : '8px 0', borderRadius: 8, border: 'none', background: next === 'dispatched' ? BLUE : GREEN, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >
                    {next === 'dispatched' ? '🔵 Mark Dispatched' : '✅ Mark Delivered'}
                  </button>
                )}
                {(status === 'pending' || status === 'dispatched') && (
                  <button
                    onClick={e => handleFail(doc, e)}
                    disabled={updateStatus.isLoading}
                    style={{ padding: isMobile ? '12px 16px' : '8px 14px', borderRadius: 8, border: `1.5px solid ${RED}`, background: '#fff', color: RED, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >
                    ❌
                  </button>
                )}
                {status === 'delivered' && (
                  <div style={{ flex: 1, textAlign: 'center', padding: '8px 0', fontSize: 13, color: GREEN, fontWeight: 700 }}>✅ Delivered</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedDoc && (
        <DetailPanel
          doc={selectedDoc}
          driverName={driverName}
          drivers={drivers}
          isDriver={isDriver}
          isMobile={isMobile}
          onClose={() => setSelectedId(null)}
          onUpdateStatus={(status) => updateStatus.mutate({ id: selectedDoc._id, status })}
          onAssignDriver={(driver_id) => assignDriver.mutate({ id: selectedDoc._id, driver_id })}
          busy={updateStatus.isLoading}
        />
      )}
    </>
  );
}

/* ─── Driver Summary Tab ────────────────────────────────────────────────── */
function SummaryTab({ rawDocs, drivers, isLoading, isMobile }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [date, setDate] = useState(todayLocal());
  const [mode, setMode] = useState('date'); // 'date' = one day | 'unsettled' = all unpaid, any date
  const [selectedDriverId, setSelectedDriverId] = useState('all');

  // fetch docs: by date, or every unpaid delivery order regardless of date
  const { data: dateDocs = [], isLoading: loadingDate } = useQuery(
    ['driver-summary-docs', mode, date],
    () => {
      const url = mode === 'unsettled'
        ? `/api/documents?type=200&status=confirmed`
        : `/api/documents?type=200&status=confirmed&date_from=${date}&date_to=${date}`;
      return api.get(url).then(r => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.data || []);
        let list = arr.filter(d => d?.delivery?.enabled);
        if (mode === 'unsettled') list = list.filter(d => Number(d.totals?.balance) > 0);
        return list;
      });
    },
    { keepPreviousData: true }
  );

  // payment methods — for recording settlement payments (default cash)
  const { data: methods = [] } = useQuery(
    ['payment-methods'],
    () => api.get('/api/payment-methods').then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])),
  );
  const cashMethod = methods.find(m => m.is_default) || methods.find(m => m.type === 'cash') || methods[0];

  // settle: record a cash payment for the full balance on each delivered, unpaid doc
  const settle = useMutation(
    async ({ docsToSettle, driver, method }) => {
      const sum = docsToSettle.reduce((s, d) => s + Number(d.totals?.balance || 0), 0);
      for (const d of docsToSettle) {
        await api.post(`/api/documents/${d._id}/payments`, {
          payment_method_id: method._id,
          amount: Number(d.totals?.balance || 0),
          currency: d.currency || 'USD',
          notes: 'Driver settlement',
        });
      }
      return { count: docsToSettle.length, sum, driver };
    },
    {
      onSuccess: ({ count, sum, driver }) => {
        alert(`✅ Settled ${count} order${count !== 1 ? 's' : ''}.`);
        qc.invalidateQueries(['driver-summary-docs']);
        qc.invalidateQueries(['delivery-docs']);
        // Push notification to driver
        if (driver?._id) {
          const cashierName = user?.name || 'Manager';
          api.post('/api/push/send', {
            user_id: driver._id,
            title: '✅ Settlement Done',
            body: `$${money(sum)} settled by ${cashierName} — ${count} order${count !== 1 ? 's' : ''}`,
            url: '/delivery',
          }).catch(() => {});
        }
      },
      onError: (e) => alert(e?.response?.data?.error || e.message),
    }
  );

  const handleSettle = (driver, docs) => {
    const settleable = docs.filter(d => d.delivery?.status === 'delivered' && Number(d.totals?.balance) > 0);
    if (settleable.length === 0) return;
    if (!cashMethod) { alert('No payment method found. Add one in Settings first.'); return; }
    const sum = settleable.reduce((s, d) => s + Number(d.totals?.balance || 0), 0);
    if (!window.confirm(
      `Settle ${driver.name}?\n\n` +
      `${settleable.length} delivered order${settleable.length !== 1 ? 's' : ''}\n` +
      `Cash to collect: $${money(sum)}\n` +
      `Payment method: ${cashMethod.name}\n\n` +
      `Only press OK after counting the cash.`
    )) return;
    settle.mutate({ docsToSettle: settleable, driver, method: cashMethod });
  };

  const grouped = useMemo(() => {
    const map = {};
    map['unassigned'] = { driver: { _id: 'unassigned', name: 'Unassigned' }, docs: [] };
    drivers.forEach(dr => { map[String(dr._id)] = { driver: dr, docs: [] }; });

    dateDocs.forEach(doc => {
      const dId = doc.delivery?.driver_id ? String(doc.delivery.driver_id) : 'unassigned';
      if (!map[dId]) map[dId] = { driver: { _id: dId, name: 'Unknown Driver' }, docs: [] };
      map[dId].docs.push(doc);
    });

    return Object.values(map).filter(g => g.docs.length > 0);
  }, [dateDocs, drivers]);

  const filteredGroups = useMemo(() =>
    selectedDriverId === 'all' ? grouped : grouped.filter(g => String(g.driver._id) === selectedDriverId),
    [grouped, selectedDriverId]
  );

  const grandTotals = useMemo(() => {
    const all = filteredGroups.flatMap(g => g.docs);
    return {
      count:     all.length,
      total:     all.reduce((s, d) => s + Number(d.totals?.total   || 0), 0),
      collected: all.reduce((s, d) => s + Number(d.totals?.paid    || 0), 0),
      balance:   all.reduce((s, d) => s + Number(d.totals?.balance || 0), 0),
    };
  }, [filteredGroups]);

  const loading = isLoading || loadingDate;

  return (
    <>
      {/* Mode toggle + filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #e5e7eb' }}>
          <button
            onClick={() => setMode('date')}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none', background: mode === 'date' ? GREEN : '#fff', color: mode === 'date' ? '#fff' : '#374151' }}
          >
            📅 By date
          </button>
          <button
            onClick={() => setMode('unsettled')}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none', background: mode === 'unsettled' ? RED : '#fff', color: mode === 'unsettled' ? '#fff' : '#374151' }}
          >
            💰 All unsettled
          </button>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginLeft: isMobile ? 0 : 'auto', width: isMobile ? '100%' : 'auto' }}>
          <select
            value={selectedDriverId}
            onChange={e => setSelectedDriverId(e.target.value)}
            style={{ flex: isMobile ? 1 : 'none', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 13, background: '#fff', color: '#111', outline: 'none' }}
          >
            <option value="all">All Drivers</option>
            {drivers.map(dr => <option key={dr._id} value={dr._id}>{dr.name}</option>)}
          </select>
          {mode === 'date' && (
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{ flex: isMobile ? 1 : 'none', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 13, background: '#fff', color: '#111', outline: 'none' }}
            />
          )}
        </div>
      </div>

      {/* Grand totals */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Total Orders', value: grandTotals.count,     fmt: v => v,    color: BLUE },
          { label: 'Total Value',  value: grandTotals.total,     fmt: v => `$${money(v)}`, color: GREEN },
          { label: 'Collected',    value: grandTotals.collected, fmt: v => `$${money(v)}`, color: GREEN },
          { label: 'Outstanding',  value: grandTotals.balance,   fmt: v => `$${money(v)}`, color: grandTotals.balance > 0 ? RED : GREEN },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: '12px 16px', borderLeft: `3px solid ${c.color}` }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</div>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#111' }}>{loading ? '…' : c.fmt(c.value)}</div>
          </div>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 48, fontSize: 14 }}>Loading…</div>}

      {!loading && filteredGroups.length === 0 && (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: 48, fontSize: 14, background: '#fff', borderRadius: 12, border: '1.5px solid #f0f0f0' }}>
          {mode === 'unsettled' ? '🎉 All delivery orders are settled — no cash outstanding.' : 'No delivery orders found for this date.'}
        </div>
      )}

      {!loading && filteredGroups.map(({ driver, docs }) => {
        const dTotal     = docs.reduce((s, d) => s + Number(d.totals?.total   || 0), 0);
        const dCollected = docs.reduce((s, d) => s + Number(d.totals?.paid    || 0), 0);
        const dBalance   = docs.reduce((s, d) => s + Number(d.totals?.balance || 0), 0);

        const settleable = docs.filter(d => d.delivery?.status === 'delivered' && Number(d.totals?.balance) > 0);
        const settleSum  = settleable.reduce((s, d) => s + Number(d.totals?.balance || 0), 0);

        const statusCounts = docs.reduce((acc, d) => {
          const s = d.delivery?.status || 'pending';
          acc[s] = (acc[s] || 0) + 1;
          return acc;
        }, {});

        return (
          <div key={driver._id} style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
            {/* Driver header */}
            <div style={{ padding: '14px 18px', borderBottom: '1.5px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#065f46', flexShrink: 0 }}>
                  {driver.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{driver.name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {docs.length} order{docs.length !== 1 ? 's' : ''}
                    {Object.entries(statusCounts).map(([s, n]) => {
                      const ss = STATUS_CONFIG[s] || {};
                      return (
                        <span key={s} style={{ marginLeft: 8, padding: '1px 7px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: ss.bg || '#f3f4f6', color: ss.color || '#6b7280' }}>
                          {n} {s}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                {[
                  { label: 'Value',       val: dTotal,     color: '#111' },
                  { label: 'Collected',   val: dCollected, color: '#065f46' },
                  { label: 'Outstanding', val: dBalance,   color: dBalance > 0 ? '#dc2626' : '#065f46' },
                ].map(c => (
                  <div key={c.label} style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{c.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: c.color }}>${money(c.val)}</div>
                  </div>
                ))}
                {settleable.length > 0 && driver._id !== 'unassigned' && (
                  <button
                    onClick={() => handleSettle(driver, docs)}
                    disabled={settle.isLoading}
                    style={{
                      padding: '9px 16px', borderRadius: 8, border: 'none',
                      background: GREEN, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                      opacity: settle.isLoading ? 0.6 : 1,
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    💵 Settle ${money(settleSum)} ({settleable.length})
                  </button>
                )}
              </div>
            </div>

            {/* Orders table — horizontal scroll on mobile */}
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    {['#', 'Date', 'Address', 'Phone', 'Total', 'Collected', 'Balance', 'Status'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {docs.map((doc, idx) => {
                    const st = doc.delivery?.status || 'pending';
                    const ss = STATUS_CONFIG[st] || {};
                    return (
                      <tr
                        key={doc._id}
                        onClick={() => navigate(`/documents/${doc._id}`)}
                        style={{ borderBottom: idx < docs.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', background: idx % 2 ? '#fafafa' : '#fff' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                        onMouseLeave={e => e.currentTarget.style.background = idx % 2 ? '#fafafa' : '#fff'}
                      >
                        <td style={{ padding: '9px 14px', fontWeight: 600, color: '#111' }}>{doc.number || '—'}</td>
                        <td style={{ padding: '9px 14px', color: '#6b7280', whiteSpace: 'nowrap' }}>{doc.date ? new Date(doc.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}</td>
                        <td style={{ padding: '9px 14px', color: '#374151', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.delivery?.address || '—'}</td>
                        <td style={{ padding: '9px 14px', color: '#374151' }}>{doc.delivery?.phone || '—'}</td>
                        <td style={{ padding: '9px 14px', fontWeight: 600, color: '#111' }}>${money(doc.totals?.total)}</td>
                        <td style={{ padding: '9px 14px', fontWeight: 600, color: '#065f46' }}>${money(doc.totals?.paid)}</td>
                        <td style={{ padding: '9px 14px', fontWeight: 600, color: Number(doc.totals?.balance) > 0 ? '#dc2626' : '#065f46' }}>${money(doc.totals?.balance)}</td>
                        <td style={{ padding: '9px 14px' }}>
                          <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: ss.bg || '#f3f4f6', color: ss.color || '#6b7280' }}>{st}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '1.5px solid #e5e7eb', background: '#f8f9fa' }}>
                    <td colSpan={4} style={{ padding: '9px 14px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>TOTAL — {driver.name}</td>
                    <td style={{ padding: '9px 14px', fontWeight: 700, color: '#111' }}>${money(dTotal)}</td>
                    <td style={{ padding: '9px 14px', fontWeight: 700, color: '#065f46' }}>${money(dCollected)}</td>
                    <td style={{ padding: '9px 14px', fontWeight: 700, color: dBalance > 0 ? '#dc2626' : '#065f46' }}>${money(dBalance)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
}

/* ─── Detail Panel ──────────────────────────────────────────────────────── */
function DetailPanel({ doc, driverName, drivers, isDriver, isMobile, onClose, onUpdateStatus, onAssignDriver, busy }) {
  const status = doc.delivery?.status || 'pending';
  const cfg    = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const next   = NEXT_STATUS[status];
  const fee    = Number(doc.delivery?.fee) || 0;

  const { data: fullDoc, isLoading: itemsLoading } = useQuery(
    ['delivery-doc-items', doc._id],
    () => api.get(`/api/documents/${doc._id}`).then(r => r.data),
    { staleTime: 30000 },
  );

  const items = fullDoc?.items || [];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.3)' }} onClick={onClose}>
      <div
        style={{
          width: isMobile ? '100vw' : 400, maxWidth: '100vw', height: '100vh',
          background: '#fff', overflowY: 'auto',
          padding: isMobile ? '16px 16px 32px' : 24,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111' }}>Order {doc.number || doc._id}</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{fmtDate(doc.createdAt)}</div>
          </div>
          <button onClick={onClose} style={{ fontSize: 24, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>✕</button>
        </div>

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 20, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 700, fontSize: 13, marginBottom: 20 }}>
          {cfg.emoji} {cfg.label}
        </div>

        <Section title="Delivery Info">
          <Row label="Address" value={doc.delivery?.address || '—'} />
          <Row label="Phone"   value={doc.delivery?.phone ? <a href={`tel:${doc.delivery.phone}`} style={{ color: BLUE }}>{doc.delivery.phone}</a> : '—'} />
          {doc.delivery?.notes && <Row label="Notes" value={doc.delivery.notes} />}
          <Row label="Fee"     value={fee > 0 ? `$${money(fee)}` : 'No fee'} />
        </Section>

        <Section title="Driver">
          {!isDriver && drivers.length > 0 && status !== 'delivered' && status !== 'failed' ? (
            <select
              value={doc.delivery?.driver_id || ''}
              onChange={e => onAssignDriver(e.target.value)}
              style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '10px', fontSize: 13, background: '#fff' }}
            >
              <option value="">— No driver assigned —</option>
              {drivers.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
            </select>
          ) : (
            <div style={{ fontSize: 14, color: '#111', fontWeight: 600 }}>{driverName(doc.delivery?.driver_id)}</div>
          )}
        </Section>

        <Section title="Update Status">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {next && (
              <button onClick={() => onUpdateStatus(next)} disabled={busy}
                style={{ padding: '12px 0', borderRadius: 8, border: 'none', background: next === 'dispatched' ? BLUE : GREEN, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                {next === 'dispatched' ? '🔵 Mark as Dispatched' : '✅ Mark as Delivered'}
              </button>
            )}
            {(status === 'pending' || status === 'dispatched') && (
              <button onClick={() => { if (window.confirm('Mark as failed?')) onUpdateStatus('failed'); }} disabled={busy}
                style={{ padding: '12px 0', borderRadius: 8, border: `1.5px solid ${RED}`, background: '#fff', color: RED, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                ❌ Mark as Failed
              </button>
            )}
            {(status === 'delivered' || status === 'failed') && (
              <div style={{ textAlign: 'center', fontSize: 14, color: cfg.color, fontWeight: 700, padding: '10px 0' }}>
                {cfg.emoji} Order is {cfg.label}
              </div>
            )}
          </div>
        </Section>

        <Section title="Items">
          {itemsLoading ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '10px 0' }}>Loading items…</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '10px 0' }}>No items found</div>
          ) : (
            <>
              {items.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f3f4f6', gap: 8 }}>
                  <span style={{ color: '#374151' }}>{item.product_name}{item.variant_name ? ` — ${item.variant_name}` : ''} × {item.quantity}</span>
                  <span style={{ fontWeight: 600, color: '#111', whiteSpace: 'nowrap' }}>${money(item.price * item.quantity)}</span>
                </div>
              ))}
              {fee > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0' }}>
                  <span style={{ color: '#3b82f6' }}>🚚 Delivery Fee</span>
                  <span style={{ fontWeight: 600, color: '#3b82f6' }}>${money(fee)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, padding: '8px 0 0', borderTop: '1px solid #e5e7eb', marginTop: 4 }}>
                <span>Total</span>
                <span>${money(items.reduce((s, i) => s + i.price * i.quantity, 0) + fee)}</span>
              </div>
            </>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{title}</div>
      <div style={{ background: '#f9fafb', borderRadius: 10, padding: 12 }}>{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', gap: 8 }}>
      <span style={{ color: '#6b7280', flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, color: '#111', textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}